'use client';

import { TwitterImageResponseSchema } from '@sweetistics/contracts/user';
import { useQuery } from '@tanstack/react-query';
import type { DOMNode, HTMLReactParserOptions } from 'html-react-parser';
import parse, { domToReact, type Element } from 'html-react-parser';
import Image from 'next/image';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { UserPreview } from '@/components/shared/previews/UserPreview';
import { Avatar } from '@/components/ui/avatar';
import { lookupProfileByUsername } from '@/lib/api/profile-client';
import { logDebug, logError, logWarning } from '@/lib/logging-utils';
import { createTrpcClient, type TrpcClient } from '@/lib/trpc/base';
import { cn } from '@/lib/utils';
import { stripHandlePrefixes } from '@/lib/utils/regex';
import { type MentionProfile, shouldFetchMentionProfile } from './tweet-text-utils';

const TWITTER_IMAGE_SLUG_PATTERN = /pic\.(?:x|twitter)\.com\/([a-zA-Z0-9_-]+)/;

interface TweetTextWithPreviewsProperties {
  className?: string;
  html: string;
  mentionProfiles?: Map<string, { username: string; name?: string | null; profileImageUrl?: string | null }>;
}

type AnchorProperties = Record<string, string | undefined> & { href?: string };

const isRenderableDomNode = (node: unknown): node is DOMNode => {
  if (node === null || node === undefined) {
    return false;
  }
  if (typeof node === 'boolean') {
    return node;
  }
  if (typeof node === 'number') {
    return node !== 0;
  }
  if (typeof node === 'string') {
    return node.length > 0;
  }
  return true;
};

const toDomNodeArray = (children: unknown): DOMNode[] => {
  if (!Array.isArray(children)) {
    return [];
  }
  return children.filter((node): node is DOMNode => isRenderableDomNode(node));
};

let cachedTwitterImageClient: TrpcClient | null = null;

const getTwitterImageClient = (): TrpcClient => {
  if (!cachedTwitterImageClient) {
    cachedTwitterImageClient = createTrpcClient();
  }
  return cachedTwitterImageClient;
};

// Component for rendering Twitter images
function TwitterImage({ url, displayUrl }: { url: string; displayUrl: string }) {
  const slugFromUrl = useMemo(() => {
    const fromUrl = url.match(TWITTER_IMAGE_SLUG_PATTERN);
    const fromDisplay = displayUrl.match(TWITTER_IMAGE_SLUG_PATTERN);
    return fromUrl?.[1] || fromDisplay?.[1] || null;
  }, [displayUrl, url]);

  const [imageErrored, setImageErrored] = useState(false);
  const derivedImageUrl = slugFromUrl ? `https://pbs.twimg.com/media/${slugFromUrl}?format=jpg&name=large` : undefined;
  const imageQuery = useQuery<string | null>({
    queryKey: ['twitter-image', url],
    queryFn: async () => {
      try {
        logDebug('twitter-image: resolving via API', { url, displayUrl }, 'ui');
        const client = getTwitterImageClient();
        const response = await client.userFeatures.getTwitterImage.query({ url });
        const parsed = TwitterImageResponseSchema.parse(response);
        if (parsed.imageUrl) {
          logDebug('twitter-image: resolved to twimg', { url, imageUrl: parsed.imageUrl }, 'ui');
          return parsed.imageUrl;
        }
      } catch (error) {
        logError('twitter-image: resolver fetch error', { error, url }, 'ui');
      }

      if (slugFromUrl) {
        const derived = `https://pbs.twimg.com/media/${slugFromUrl}?format=jpg&name=large`;
        logDebug('twitter-image: derived slug fallback', { slug: slugFromUrl, derived }, 'ui');
        return derived;
      }

      logWarning('twitter-image: resolver returned null and no slug found', { url, displayUrl }, 'ui');
      return null;
    },
    initialData: derivedImageUrl ?? null,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: 1,
    enabled: !imageErrored,
  });
  const imageUrl = imageQuery.data ?? null;
  const isError = imageQuery.isError;

  if (isError || !imageUrl || imageErrored) {
    // Fallback to showing the link
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
        {displayUrl}
      </a>
    );
  }

  return (
    <div className="mt-2 mb-2">
      <a href={url} target="_blank" rel="noopener noreferrer" className="block">
        <div className="relative rounded-xl overflow-hidden max-w-full">
          <Image
            src={imageUrl}
            alt="Tweet image"
            width={550}
            height={350}
            className="w-full h-auto object-cover"
            onError={() => {
              logWarning('twitter-image: next/image onError', { imageUrl, url }, 'ui');
              setImageErrored(true);
            }}
            unoptimized // Twitter images should not be optimized through Next.js
          />
        </div>
      </a>
    </div>
  );
}

interface MentionAnchorProperties {
  anchorProps: AnchorProperties;
  children: ReactNode;
  className: string;
  mentionProfiles?: Map<string, MentionProfile>;
  username: string;
}

function sanitizeUsername(raw: string | undefined): string {
  if (!raw) {
    return '';
  }
  return stripHandlePrefixes(raw.trim());
}

function MentionAnchor({ username, className, anchorProps, children, mentionProfiles }: MentionAnchorProperties) {
  const normalized = useMemo(() => sanitizeUsername(username).toLowerCase(), [username]);
  const mentionProfile = normalized ? (mentionProfiles?.get(normalized) ?? null) : null;

  const shouldFetchProfile = shouldFetchMentionProfile(mentionProfile, normalized);

  const profileQuery = useQuery({
    queryKey: ['mention-profile', normalized],
    queryFn: async () => {
      logDebug('mention-profile: fetching profile by username', { username: normalized }, 'ui');
      try {
        // lookupProfileByUsername now consults the shared user-preview cache first, so repeated
        // mention avatars across the archive/timeline surfaces reuse the same payload.
        const result = await lookupProfileByUsername(normalized);
        return {
          username: result.username ?? username,
          name: result.name ?? result.username ?? username,
          profileImageUrl: result.profileImageUrl ?? null,
        } satisfies MentionProfile;
      } catch (error) {
        logWarning('mention-profile: failed to fetch profile', { username: normalized, error }, 'ui');
        throw error instanceof Error ? error : new Error('Failed to fetch mention profile');
      }
    },
    enabled: shouldFetchProfile,
    staleTime: 15 * 60_000,
    gcTime: 60 * 60_000,
    retry: false,
  });

  const profile: MentionProfile = useMemo(() => {
    const fetchedProfile = profileQuery.data;
    if (fetchedProfile && (fetchedProfile.profileImageUrl || fetchedProfile.name)) {
      return fetchedProfile;
    }
    if (mentionProfile && (mentionProfile.profileImageUrl || mentionProfile.name)) {
      return mentionProfile;
    }
    return {
      username,
      name: mentionProfile?.name ?? username,
      profileImageUrl: mentionProfile?.profileImageUrl ?? null,
    };
  }, [mentionProfile, profileQuery.data, username]);

  const displayUsername = profile.username ?? username ?? '';
  const displayName = profile.name ?? `@${displayUsername}`;
  const fallbackInitial = displayUsername ? (displayUsername.charAt(0)?.toUpperCase() ?? '?') : '?';

  return (
    <UserPreview username={displayUsername}>
      <a
        {...anchorProps}
        className={className}
        data-preview="user"
        data-username={displayUsername}
        href={anchorProps.href ?? `https://twitter.com/${displayUsername}`}
      >
        <span className="truncate">{children}</span>
        <Avatar
          src={profile.profileImageUrl ?? undefined}
          alt={displayName}
          size={16}
          className="flex-shrink-0 align-baseline transform translate-y-[3px]"
          fallbackText={fallbackInitial}
        />
      </a>
    </UserPreview>
  );
}

export function TweetTextWithPreviews({ html, className = '', mentionProfiles }: TweetTextWithPreviewsProperties) {
  // Parse options to declaratively transform HTML elements
  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      // Type guard to check if it's an Element with proper validation
      if (!isElement(domNode)) {
        return;
      }

      const element = domNode;

      // Handle user preview links
      if (element.name === 'a' && element.attribs['data-preview'] === 'user') {
        const username = element.attribs['data-username'];
        if (!username) {
          return;
        }

        const labelChildren = toDomNodeArray(element.children);
        const label =
          labelChildren.length > 0 ? domToReact(labelChildren, parserOptions) : `@${sanitizeUsername(username)}`;
        const classAttribute = (element.attribs as typeof element.attribs & { class?: string }).class;
        const anchorClass = cn(classAttribute, 'inline-flex flex-row-reverse items-baseline gap-1 align-baseline');
        const anchorProperties: AnchorProperties = {};
        for (const [key, value] of Object.entries(element.attribs)) {
          if (key === 'class') {
            continue;
          }
          anchorProperties[key] = value;
        }

        return (
          <MentionAnchor
            username={username}
            className={anchorClass}
            anchorProps={anchorProperties}
            {...(mentionProfiles ? { mentionProfiles } : {})}
          >
            {label}
          </MentionAnchor>
        );
      }

      // Handle Twitter image placeholders
      const elementClass = (element.attribs as typeof element.attribs & { class?: string }).class;
      if (typeof elementClass === 'string' && elementClass.includes('twitter-image-placeholder')) {
        const url = element.attribs['data-twitter-image-url'];
        const displayUrl = element.attribs['data-display-url'];

        if (!(url && displayUrl)) {
          return;
        }

        return <TwitterImage url={url} displayUrl={displayUrl} />;
      }

      // Return undefined to keep the element as-is
      return;
    },
  };

  // Parse the HTML with our replacement rules
  const parsedContent = parse(html, parserOptions);

  return (
    <div
      className={cn(
        'w-full min-w-0 max-w-full whitespace-pre-wrap break-words [word-break:break-word] [overflow-wrap:anywhere]',
        className
      )}
    >
      {parsedContent}
    </div>
  );
}

function isElement(node: DOMNode): node is Element {
  return typeof node === 'object' && node !== null && 'type' in node && (node as { type?: unknown }).type === 'tag';
}
